import { Ban, Info, Stamp } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useListDocuments } from "~/api/default/default";
import { Button } from "~/components/ui/button";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import {
  isBatesEligible,
  useApplyBates,
  useIsBatesEnabled,
  useUpdateBatesStatus,
} from "../../hooks/useBatesActions";
import { ApplyBatesConfirmDialog } from "./ApplyBatesConfirmDialog";

/**
 * Amber info banner shown above the document list when some files
 * still need a Bates decision (Apply or Skip).
 */
export const BatesPendingBanner: FC = () => {
  const { organizationId, caseId } = useProjectParams();
  const { formatMessage } = useIntl();
  const { toast } = useToast();
  const isBatesEnabled = useIsBatesEnabled();
  const { data: documentsList } = useListDocuments(organizationId, caseId);
  const { mutateAsync: applyBates, isPending: isApplying } = useApplyBates();
  const { mutateAsync: updateBatesStatus, isPending: isSkipping } =
    useUpdateBatesStatus();
  const [showApplyBatesDialog, setShowApplyBatesDialog] = useState(false);

  const eligibleDocumentIds = useMemo(() => {
    if (!documentsList?.documents) return [];
    return documentsList.documents
      .filter((doc) => isBatesEligible(doc))
      .map((doc) => doc.id);
  }, [documentsList]);

  const totalDocuments = documentsList?.documents?.length ?? 0;

  if (!isBatesEnabled || eligibleDocumentIds.length === 0) {
    return null;
  }

  const isBusy = isApplying || isSkipping;

  const handleApplyAll = async () => {
    try {
      await applyBates(eligibleDocumentIds);
      toast({
        title: formatMessage({
          id: "bates.applySuccess.title",
          defaultMessage: "Bates Numbers Applied",
        }),
        description: formatMessage({
          id: "bates.applySuccess.description",
          defaultMessage: "Stamping in progress — numbers will appear shortly.",
        }),
      });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "bates.applyError.title",
          defaultMessage: "Failed to Apply Bates Numbers",
        }),
        description: formatMessage({
          id: "bates.applyError.description",
          defaultMessage:
            "Some documents may already be stamped or still processing.",
        }),
      });
    }
  };

  const handleSkipAll = async () => {
    try {
      await updateBatesStatus({
        documentIds: eligibleDocumentIds,
        status: "SKIPPED",
      });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "bates.actionError.title",
          defaultMessage: "Bates Action Failed",
        }),
        description: formatMessage({
          id: "bates.actionError.description",
          defaultMessage: "Could not update Bates status. Please try again.",
        }),
      });
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-2.5 mt-2">
        <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <span className="text-sm text-amber-800 dark:text-amber-200 flex-1">
          <FormattedMessage
            id="bates.pendingBanner.message"
            defaultMessage="{count} of {total} {total, plural, one {file} other {files}} still {count, plural, one {needs} other {need}} a Bates decision"
            values={{
              count: eligibleDocumentIds.length,
              total: totalDocuments,
            }}
          />
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
            onClick={() => setShowApplyBatesDialog(true)}
            disabled={isBusy}
          >
            <Stamp className="w-3.5 h-3.5 mr-1" />
            <FormattedMessage
              id="bates.pendingBanner.applyAll"
              defaultMessage="Apply All"
            />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleSkipAll}
            disabled={isBusy}
          >
            <Ban className="w-3.5 h-3.5 mr-1" />
            <FormattedMessage
              id="bates.pendingBanner.skipAll"
              defaultMessage="Skip All"
            />
          </Button>
        </div>
      </div>
      {showApplyBatesDialog && (
        <ApplyBatesConfirmDialog
          open={showApplyBatesDialog}
          onOpenChange={setShowApplyBatesDialog}
          documentCount={eligibleDocumentIds.length}
          onConfirm={handleApplyAll}
        />
      )}
    </>
  );
};
